/**
 * Game detail V2 — the FRIENDS panel (`design/Games Tonight.dc.html` 606-666),
 * its collapsed tabs (:563-571) and its narrow-width drawer.
 *
 * THE MIRROR CANNOT BE PORTED LITERALLY HERE, and this is the one component in
 * the lane where that is true of the markup rather than of a string:
 *
 * - Both collapsed-tab blocks are gated on `<sc-if value="{{ neverShow }}">`,
 *   and `neverShow` is hardcoded `false` at :3326. Copying the markup ships a
 *   panel that can be closed and never reopened.
 * - The two panel divs hardcode `width:340px` and ignore all seven
 *   `panelPos/Top/Right/Bottom/Z/W/Sh` fields their own view-model computes at
 *   :3192-3198. Copying them ships a permanently static column with no drawer.
 *
 * So this ports the VIEW-MODEL'S INTENT: a static third column at ≥1080px, the
 * same panel as an absolute drawer below it, and a tab when collapsed.
 *
 * `(S.w || 9999) >= 1080` DOES NOT PORT AS AN EXPRESSION — and would invert if
 * it did. There is no width number in `app/src`, and `readBreakpoint` answers
 * `false` when it cannot tell, where the prototype's `9999` answers wide. What
 * the guard protected is the INVARIANT that a falsy width must never hide both
 * the panel and its tab, and that ports exactly: `open` is one boolean and the
 * two states are its two branches, so exactly one always renders.
 *
 * ESCAPE, NARROW ONLY. At ≥1080 the panel is a column — Escape closing a column
 * would be Escape rearranging the page. Below it, the panel is an overlay and
 * joins the shared stack through `useEscape`, which means ordinary LIFO: a modal
 * opened over the drawer closes first, then the drawer. The prototype's changelog
 * asserts absolute precedence and its own code comment (:1509-1511) contradicts
 * it in favour of LIFO; the comment is right and is what the app already does
 * everywhere else.
 *
 * THE ROSTER KEEPS ITS LIBRARY COLUMN, which the mirror has not got. That is not
 * the app running ahead of the design: the V2 changelog entry specifies the row
 * as "avatar · name · their call · library state" and the export simply dropped
 * it, the same way it dropped `fitTxt`. Four states, three of which carry the
 * hazard — see `libState`.
 */

import { Avatar } from '../../components/Avatar.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { useQd } from '../../store/index.ts';
import { NO_STEAM_TIP, quickAddBlocked } from '../../store/selectors.3c.ts';
import type { DetailVoteRow } from '../../store/selectors.desktop.ts';
import type { DetailVM } from '../../store/selectors.ts';
import { useBreakpoint } from '../useBreakpoint.ts';
import { useEscape } from './Modal.tsx';
import type { CSSProperties } from 'react';

const MONO = 'var(--gt-font-mono)';

/** ≥ this and the panel is a column; below it, a drawer over the middle pane. */
const WIDE = '(min-width: 1080px)';

/** Both tabs sit in the shell's top-right corner, out of the row flow. */
const TAB: CSSProperties = {
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 6,
  display: 'flex',
  alignItems: 'center',
  background: 'var(--gt-panel)',
  cursor: 'pointer',
};

/** One roster, not two lists: avatar · name · the call over the ownership line. */
function Roster({ rows }: { rows: DetailVoteRow[] }): JSX.Element {
  return (
    <div style={{ borderBottom: '1px solid var(--gt-line)', display: 'flex', flexDirection: 'column', flex: 'none' }}>
      {rows.map((r) => (
        <div
          key={r.key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '9px 18px',
            borderBottom: '1px solid var(--gt-line-0)',
          }}
        >
          <Avatar style={{ flex: 'none' }} size={24} fs={7.5} ini={r.ini} bg={r.hueBg} hasAv={r.hasAv} avBg={r.avBg} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: MONO,
              fontSize: 10,
              color: 'var(--gt-text-2)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {r.name}
          </span>
          {/* The call and the library state STACK rather than sharing a line.
              The mirror puts the call alone on one row, and 340px does not
              hold `B · I COULD BE CONVINCED` beside `NO STEAM LINK` beside a
              name. Both facts are spec'd, so the column wins over the row. */}
          <span style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end', flex: 'none' }}>
            {r.lvlTxt ? (
              <span style={{ fontFamily: MONO, fontSize: 9.5, letterSpacing: '.05em', color: r.lvlClr, whiteSpace: 'nowrap' }}>
                {r.lvlTxt}
              </span>
            ) : null}
            {/* Blank until the ownership RPC answers — an unanswered question
                must never paint as an answer. `libState` holds the four states
                and the reasons three of them are hazardous. */}
            {r.libTxt ? (
              <span style={{ fontFamily: MONO, fontSize: 8.5, letterSpacing: '.05em', color: r.libClr, whiteSpace: 'nowrap' }}>
                {r.libTxt}
              </span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

export function DetailFriendsPanel({ d, rows }: { d: DetailVM; rows: DetailVoteRow[] }): JSX.Element {
  const s = useQd();
  const wide = useBreakpoint(WIDE);
  const open = s.dPanel ?? wide;
  // PORTING-NOTES D-02: a CheapShark-only row has no appid the list FK can hold,
  // so the add target is inert and says why. The desktop screen no longer has a
  // primary CTA of its own, which makes THIS the only place the gate binds on
  // this tree — do not delete it without moving it.
  const addBlocked = !d.inList && quickAddBlocked(d.id);

  // Narrow only: see the header note.
  useEscape(() => s.togglePanel(wide), !wide && open);

  const toggle = (): void => s.togglePanel(wide);

  const onListBtn = (): void => {
    if (!d.inList) {
      if (addBlocked) return;
      void s.quickAdd(d.id);
      return;
    }
    // Arms, then acts. Removing takes the game off the list for the whole crew.
    if (!s.listRmArm) {
      s.setListRmArm(true);
      return;
    }
    s.setListRmArm(false);
    void s.removeFromList(d.id);
  };

  if (!open) {
    return d.inList ? (
      <div
        onClick={toggle}
        data-tip="SHOW THE PARTY PANEL"
        style={{ ...TAB, gap: 11, border: '1px solid var(--gt-border)', borderTop: 0, borderRight: 0, padding: '10px 13px' }}
      >
        <span style={{ color: 'var(--gt-text-3)', fontFamily: MONO, fontSize: 17, lineHeight: 1 }}>◂</span>
        <span
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: 13,
            background: d.groupBg,
            color: 'var(--gt-ink)',
            padding: '9px 10px',
            lineHeight: 1,
          }}
        >
          <span style={{ display: 'block', transform: 'translateY(0.13em)' }}>{d.groupBig}</span>
        </span>
      </div>
    ) : (
      <div
        onClick={toggle}
        /* The mirror tips this `NOT ON TONIGHT'S LIST — ADD IT` unconditionally,
           which on a blocked row invites a click that opens a panel whose only
           control is inert. Say the real reason instead. */
        data-tip={addBlocked ? NO_STEAM_TIP : "NOT ON TONIGHT'S LIST — ADD IT"}
        style={{
          ...TAB,
          justifyContent: 'center',
          width: 52,
          height: 52,
          border: '1px dashed var(--gt-border-3)',
          borderTop: 0,
          borderRight: 0,
          color: 'var(--gt-text-5)',
          fontFamily: MONO,
          fontSize: 19,
          lineHeight: 1,
        }}
      >
        +
      </div>
    );
  }

  const panel: CSSProperties = {
    width: wide ? 340 : 380,
    flex: 'none',
    minHeight: 0,
    overflowY: 'auto',
    borderLeft: '1px solid var(--gt-border)',
    background: 'var(--gt-panel)',
    display: 'flex',
    flexDirection: 'column',
    ...(wide
      ? null
      : {
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 30,
          boxShadow: '-12px 12px 0 rgba(0,0,0,.55)',
        }),
  };

  if (!d.inList) {
    return (
      <div style={panel}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '13px 12px 13px 18px',
            background: 'var(--gt-raised)',
            borderBottom: '1px solid var(--gt-border)',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: 9.5,
              color: 'var(--gt-text-5)',
              transform: 'translateY(0.13em)',
            }}
          >
            NOT ON THE LIST
          </span>
          <div style={{ flex: 1 }} />
          <span
            onClick={toggle}
            data-tip="CLOSE THE PARTY PANEL"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 30,
              height: 30,
              flex: 'none',
              color: 'var(--gt-text-5)',
              fontFamily: MONO,
              fontSize: 15,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            ▸
          </span>
        </div>
        {/* The same roster, ownership only (2026-09-03): who owns it is a fact
            about the game whether or not it is on the list. */}
        <Roster rows={rows} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', padding: '26px 18px' }}>
          <div
            onClick={onListBtn}
            data-tip={addBlocked ? NO_STEAM_TIP : undefined}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 11,
              border: '1px dashed var(--gt-border-3)',
              padding: '26px 14px',
              cursor: addBlocked ? 'not-allowed' : 'pointer',
              boxSizing: 'border-box',
              opacity: addBlocked ? 0.55 : 1,
            }}
          >
            <span style={{ color: 'var(--acc)', fontFamily: MONO, fontSize: 22, lineHeight: 1 }}>+</span>
            <span
              style={{
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: 9,
                color: 'var(--acc)',
                transform: 'translateY(0.13em)',
              }}
            >
              ADD TO GAMES TONIGHT?
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={panel}>
      {/* The verdict bar IS the header AND the collapse toggle. The mirror's is
          a header only; making it the toggle is this port's decision, and it is
          what lets the panel collapse without spending a row on a control. */}
      <div
        onClick={toggle}
        data-tip="CLOSE THE PARTY PANEL"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 13,
          padding: '13px 12px 13px 18px',
          background: 'var(--gt-raised)',
          borderBottom: '1px solid var(--gt-border)',
          cursor: 'pointer',
          flex: 'none',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: 13,
            background: d.groupBg,
            color: 'var(--gt-ink)',
            padding: '11px 13px',
            flex: 'none',
            transform: 'translateY(0.13em)',
          }}
        >
          {d.groupBig}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7, flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: 9.5,
              lineHeight: 1.5,
              color: 'var(--gt-text)',
              transform: 'translateY(0.13em)',
            }}
          >
            {d.groupSub}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)' }}>
            {d.calledTxt}
          </div>
        </div>
      </div>

      <Roster rows={rows} />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 9,
          padding: '14px 18px',
          borderBottom: '1px solid var(--gt-line)',
          flex: 'none',
        }}
      >
        <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 7.5, color: 'var(--acc)' }}>YOUR CALL</span>
        <div style={{ display: 'flex' }}>
          {d.callCells.map((c) => (
            <div
              key={c.key}
              onClick={() => void s.vote(d.id, c.key)}
              data-tip={c.tip}
              style={{
                position: c.pos,
                zIndex: c.z,
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 46,
                marginLeft: c.ml,
                border: '1px solid ' + c.bd,
                background: c.bg,
                color: c.clr,
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              <span style={{ transform: 'translateY(0.13em)' }}>{c.g}</span>
            </div>
          ))}
        </div>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: d.myClr }}>{d.myTxt}</span>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 13, padding: '14px 18px' }}>
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.11em', color: 'var(--gt-muted)' }}>
          {d.commentsTxt}
        </span>
        {d.comments.map((c) => (
          <div key={c.key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
            <Avatar style={{ flex: 'none' }} size={24} fs={7.5} ini={c.ini} bg={c.hueBg} hasAv={c.hasAv} avBg={c.avBg} />
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontFamily: MONO, fontSize: 9.5, color: 'var(--gt-text-5)' }}>
                {c.name} <span style={{ color: 'var(--gt-faint)' }}>· {c.agoTxt}</span>
              </div>
              <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--gt-text-2)', overflowWrap: 'anywhere' }}>
                {c.parts.map((p) =>
                  p.isLink ? (
                    <a key={p.key} href={p.href} target="_blank" rel="noreferrer">
                      {p.txt}
                    </a>
                  ) : (
                    <span key={p.key}>{p.txt}</span>
                  ),
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          padding: '12px 18px 16px',
          borderTop: '1px solid var(--gt-line)',
          flex: 'none',
        }}
      >
        {/* The short placeholder, adopted here rather than reverted. The long
            form told the crew what a comment is for and its loss was logged as
            a design change rather than accepted silently (DESIGN-CHANGELOG
            2026-08-13); the app kept the long form "until V2 lands". This is
            V2 landing, and the shortening is coherent with a 340px column. */}
        <textarea
          value={s.draft}
          onChange={(e) => s.setDraft(e.target.value)}
          placeholder="ADD A COMMENT…"
          rows={1}
          style={{
            flex: 1,
            minWidth: 0,
            background: 'var(--gt-field)',
            border: '1px solid var(--gt-border-2)',
            color: 'var(--gt-text)',
            fontFamily: MONO,
            fontSize: 10.5,
            padding: '11px 12px',
            outline: 'none',
            resize: 'vertical',
          }}
        />
        <PixelButton label="POST ▸" onClick={() => void s.postC(d.id, s.draft)} fs={8} pad="13px 12px" nowrap />
      </div>

      <div
        onClick={onListBtn}
        data-tip="REMOVES IT FOR THE WHOLE PARTY, NOT JUST FOR YOU"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          flex: 'none',
          padding: '13px 18px 16px',
          borderTop: '1px solid var(--gt-line)',
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: '.06em',
          color: d.listRmClr,
          cursor: 'pointer',
        }}
      >
        {d.listRmTxt}
      </div>
    </div>
  );
}
